import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import {
  leadService,
  leadStatusLabel,
  type LeadRow,
  type LeadStatus,
} from '../services/leadService';
import { fieldClass, formatDateTime, labelClass } from '../utils/form';

export function LeadsPage() {
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [hasPhone, setHasPhone] = useState<'any' | 'yes' | 'no'>('any');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<LeadRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadService.listLeads({
        page,
        limit,
        q: q.trim() || undefined,
        city: city.trim() || undefined,
        category: category.trim() || undefined,
        status: status || undefined,
        source: source.trim() || undefined,
        hasPhone: hasPhone === 'any' ? undefined : hasPhone === 'yes',
      });
      setRows(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages || 1));
      setTotal(res.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت لیدها');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, q, city, category, status, source, hasPhone]);

  useEffect(() => {
    void load();
  }, [load]);

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => selected.has(r.id)),
    [rows, selected],
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(rows.map((r) => r.id)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openDetail = (row: LeadRow) => setDetail(row);

  const saveDetail = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const updated = await leadService.patchLead(detail.id, {
        status: detail.status,
        notes: detail.notes,
      });
      setDetail(updated);
      toast.success('لید به‌روز شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`حذف ${ids.length} لید انتخاب‌شده؟ این عمل برگشت‌پذیر نیست.`)) {
      return;
    }
    setDeleting(true);
    try {
      const n = await leadService.bulkDeleteLeads(ids);
      toast.success(`${n} لید حذف شد`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">لیدها</h1>
          <p className="mt-1 text-sm text-slate-500">مدیریت کسب‌وکارهای جمع‌آوری‌شده از جاب‌های جستجو</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 ? (
            <button
              type="button"
              disabled={deleting}
              onClick={() => void deleteSelected()}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'در حال حذف…' : `حذف انتخاب‌شده‌ها (${selected.size})`}
            </button>
          ) : null}
          <Link
            to="/business-search"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700"
          >
            <Search className="h-4 w-4" />
            جستجوی کسب‌وکار
          </Link>
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <label className={labelClass}>جستجو</label>
          <input
            className={fieldClass}
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            placeholder="نام، تلفن، وب‌سایت…"
          />
        </div>
        <div>
          <label className={labelClass}>شهر</label>
          <input
            className={fieldClass}
            value={city}
            onChange={(e) => {
              setPage(1);
              setCity(e.target.value);
            }}
          />
        </div>
        <div>
          <label className={labelClass}>دسته‌بندی</label>
          <input
            className={fieldClass}
            value={category}
            onChange={(e) => {
              setPage(1);
              setCategory(e.target.value);
            }}
          />
        </div>
        <div>
          <label className={labelClass}>وضعیت</label>
          <select
            className={fieldClass}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">همه</option>
            {(Object.keys(leadStatusLabel) as LeadStatus[]).map((s) => (
              <option key={s} value={s}>
                {leadStatusLabel[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>منبع</label>
          <input
            className={fieldClass}
            value={source}
            onChange={(e) => {
              setPage(1);
              setSource(e.target.value);
            }}
            placeholder="crawler یا mock"
            dir="ltr"
          />
        </div>
        <div>
          <label className={labelClass}>شماره تلفن</label>
          <select
            className={fieldClass}
            value={hasPhone}
            onChange={(e) => {
              setPage(1);
              setHasPhone(e.target.value as 'any' | 'yes' | 'no');
            }}
          >
            <option value="any">همه</option>
            <option value="yes">دارای شماره</option>
            <option value="no">بدون شماره</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
          {loading ? 'در حال بارگذاری…' : `${total} لید`}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    aria-label="انتخاب همه"
                  />
                </th>
                <th className="px-4 py-3 text-right font-bold">نام کسب‌وکار</th>
                <th className="px-4 py-3 text-right font-bold">دسته‌بندی</th>
                <th className="px-4 py-3 text-right font-bold">شهر</th>
                <th className="px-4 py-3 text-right font-bold">موبایل</th>
                <th className="px-4 py-3 text-right font-bold">منبع</th>
                <th className="px-4 py-3 text-right font-bold">وضعیت</th>
                <th className="px-4 py-3 text-right font-bold">تاریخ</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/80">
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      aria-label={`انتخاب ${row.businessName}`}
                    />
                  </td>
                  <td className="max-w-[220px] truncate px-4 py-3 font-semibold text-slate-900">{row.businessName}</td>
                  <td className="px-4 py-3 text-slate-600">{row.category || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{row.city || '—'}</td>
                  <td className="px-4 py-3 font-mono text-slate-700" dir="ltr">
                    {row.phone || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{row.source || '—'}</td>
                  <td className="px-4 py-3">{leadStatusLabel[row.status] ?? row.status}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDateTime(row.createdAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                      title="جزئیات"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                    لیدی پیدا نشد
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100 px-4 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title="جزئیات لید"
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={() => setDetail(null)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
            >
              بستن
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void saveDetail()}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره…' : 'ذخیره'}
            </button>
          </>
        }
      >
        {detail ? (
          <div className="space-y-4" dir="rtl">
            <div>
              <p className="text-lg font-black text-slate-900">{detail.businessName}</p>
              <p className="mt-1 text-sm text-slate-500">
                {detail.category} · {detail.city}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass}>موبایل</label>
                <p className="font-mono text-sm" dir="ltr">
                  {detail.phone || '—'}
                </p>
              </div>
              <div>
                <label className={labelClass}>وب‌سایت</label>
                <p className="truncate text-sm" dir="ltr">
                  {detail.website || '—'}
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>آدرس</label>
                <p className="text-sm text-slate-700">{detail.address || '—'}</p>
              </div>
              <div>
                <label className={labelClass}>منبع</label>
                <p className="text-sm">
                  <span
                    className={
                      detail.source === 'mock'
                        ? 'rounded bg-amber-100 px-1.5 py-0.5 font-bold text-amber-800'
                        : 'rounded bg-emerald-100 px-1.5 py-0.5 font-bold text-emerald-800'
                    }
                  >
                    {detail.source === 'mock' ? 'mock (تست)' : detail.source || '—'}
                  </span>
                  {detail.sourceDomain ? ` · ${detail.sourceDomain}` : ''}
                </p>
              </div>
              {detail.sourceUrl ? (
                <div className="sm:col-span-2">
                  <label className={labelClass}>آدرس منبع</label>
                  <a
                    className="block truncate text-sm text-indigo-600 hover:underline"
                    href={detail.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    dir="ltr"
                  >
                    {detail.sourceUrl}
                  </a>
                </div>
              ) : null}
              <div>
                <label className={labelClass}>وضعیت</label>
                <select
                  className={fieldClass}
                  value={detail.status}
                  onChange={(e) =>
                    setDetail({ ...detail, status: e.target.value as LeadStatus })
                  }
                >
                  {(Object.keys(leadStatusLabel) as LeadStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {leadStatusLabel[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>یادداشت</label>
                <textarea
                  className={fieldClass}
                  rows={3}
                  value={detail.notes}
                  onChange={(e) => setDetail({ ...detail, notes: e.target.value })}
                />
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
